/**
 * Practice-name availability (HA-427).
 *
 * Every practice-scoped row is partitioned on the practice name, and nothing used to stop two
 * independent practices claiming the same one — so they ended up sharing a partition and reading
 * each other's license/billing/audit data. The signup form has always told users otherwise
 * ("It cannot be used by other practices"); this is what finally backs that promise.
 *
 * The endpoint is anonymous by necessity: signup writes custom:practice_name straight to Cognito
 * client-side, so this is the only point a duplicate can be caught while the user can still pick
 * a different name.
 */

import axios from 'axios';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';

export interface PracticeNameAvailability {
  name: string;
  available: boolean;
}

/**
 * Whether a practice name is free to claim.
 *
 * Fails OPEN — if the check itself errors (offline, API down, timeout) this resolves to
 * `available: true` so a user is never stranded on the signup form by an unavailable
 * availability check. The backend performs the authoritative check.
 */
export const checkPracticeNameAvailability = async (
  name: string
): Promise<PracticeNameAvailability> => {
  const trimmed = name.trim();

  if (!trimmed) {
    return { name: trimmed, available: false };
  }

  try {
    const response = await axios.get(`${healioSpaceAPIURL}/api/v1/practice-name/availability`, {
      params: { name: trimmed },
      timeout: 8000,
    });

    return response.data as PracticeNameAvailability;
  } catch {
    // Never block signup because the availability probe failed.
    return { name: trimmed, available: true };
  }
};
